import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiUser, FiBriefcase, FiSmile, FiGlobe, FiAlertCircle, FiCamera, FiArrowRight, FiArrowLeft, FiRefreshCw, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { createCharacter, updateCharacter, generateBackstory } from '../services/characterService';
import { generateCharacterPhoto, deletePhoto } from '../services/imageService';
import { useAuth } from '../context/AuthContext';
import Spinner from './Spinner';
import AvatarModal from './AvatarModal';

const PERSONALITY_TAGS = ['Playful', 'Sweet', 'Shy', 'Bold', 'Flirty', 'Tsundere', 'Caring', 'Sarcastic', 'Nerdy', 'Mysterious', 'Calm', 'Dominant'];
const ART_STYLES = [
  { value: 'realistic', label: 'Realistic', icon: '📷' },
  { value: 'anime', label: 'Anime', icon: '🎌' },
  { value: 'fantasy', label: 'Fantasy', icon: '✨' },
  { value: 'illustrated', label: 'Illustrated', icon: '🎨' },
];

const CharacterModal = ({ isOpen, onClose, onCharacterCreated, onCharacterUpdated, appConfig, trialStatus, characterCount, characterToEdit }) => {
  const { firebaseUser } = useAuth();

  // Track photo URLs generated during this modal session (create mode only).
  // On close without a successful submit, any unsaved URLs are deleted from storage
  // to prevent permanently orphaned generated photos.
  const sessionGeneratedUrls = useRef([]);
  const savedPhotoUrls = useRef(new Set());
  const defaultExamples = [
    { user: '', char: '' },
    { user: '', char: '' },
    { user: '', char: '' }
  ];
  const [formData, setFormData] = useState({
    characterId: '',
    name: '',
    age: '25',
    sex: 'Female',
    ethnicity: '',
    hairColor: '',
    hairStyle: '',
    eyeColor: '',
    skinColor: '',
    bodyType: '',
    clothes: '',
    looks: '',
    occupation: '',
    personality: '',
    language: 'English',
    scenario: '',
    examples: defaultExamples,
    photoUrl: '',
    fullPhotoUrl: ''
  });

  // UI State
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [limitReached, setLimitReached] = useState(false);
  
  // Carousel State
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [previewImage, setPreviewImage] = useState(null);

  // Initialize ID and check limits
  useEffect(() => {
    if (isOpen) {
      // Reset orphan-tracking refs each time the modal opens.
      sessionGeneratedUrls.current = [];
      savedPhotoUrls.current = new Set();

      if (characterToEdit) {
        // Edit Mode
        const predefinedRoles = ['romantic partner', 'best friend', 'mentor', 'rival'];
        const savedRole = characterToEdit.relationshipRole || '';
        setFormData({
          characterId: characterToEdit.characterId,
          name: characterToEdit.name,
          age: characterToEdit.age,
          sex: characterToEdit.physicalTraits?.sex || 'Female',
          ethnicity: characterToEdit.ethnicity || '',
          hairColor: characterToEdit.physicalTraits?.hairColor || '',
          hairStyle: characterToEdit.physicalTraits?.hairStyle || '',
          eyeColor: characterToEdit.physicalTraits?.eyeColor || '',
          skinColor: characterToEdit.physicalTraits?.skinColor || '',
          bodyType: characterToEdit.physicalTraits?.bodyType || '',
          clothes: characterToEdit.physicalTraits?.clothes || '',
          looks: characterToEdit.physicalTraits?.looks || '',
          occupation: characterToEdit.occupation,
          personality: characterToEdit.personality,
          language: characterToEdit.language,
          scenario: characterToEdit.scenario || '',
          examples: Array.isArray(characterToEdit.examples) && characterToEdit.examples.length > 0
            ? characterToEdit.examples.map(ex => ({ user: ex.user || '', char: ex.char || '' }))
            : defaultExamples,
          photoUrl: characterToEdit.photoUrl,
          fullPhotoUrl: characterToEdit.fullPhotoUrl || '',
          artStyle: characterToEdit.artStyle || 'realistic',
          relationshipRole: predefinedRoles.includes(savedRole) ? savedRole : (savedRole ? 'other' : ''),
          customRole: predefinedRoles.includes(savedRole) || !savedRole ? '' : savedRole,
          personalityTags: characterToEdit.personalityTags || [],
          backstory: characterToEdit.backstory || '',
        });
        setStep(1);
        setLimitReached(false);
        setCurrentImageIndex(0);
      } else {
        // Create Mode
        const newId = crypto.randomUUID();
        setFormData({
          characterId: newId,
          name: '',
          age: '25',
          sex: 'Female',
          ethnicity: '',
          hairColor: '',
          hairStyle: '',
          eyeColor: '',
          skinColor: '',
          bodyType: '',
          clothes: '',
          looks: '',
          occupation: '',
          personality: '',
          language: 'English',
          scenario: '',
          examples: defaultExamples,
          photoUrl: '',
          fullPhotoUrl: '',
          artStyle: 'realistic',
          relationshipRole: '',
          customRole: '',
          personalityTags: [],
          backstory: '',
        });
        setStep(1);
        setError('');
        setCurrentImageIndex(0);
        
        if (appConfig && trialStatus) {
          const freeLimit = appConfig?.limits?.free?.customCharacterLimit ?? 1;
          const paidLimit = appConfig?.limits?.paid?.customCharacterLimit ?? 20;
          const limit = trialStatus?.isPaidUser ? paidLimit : freeLimit;
          // characterCount includes Bela; customCharacterLimit refers to characters beyond Bela
          const customCharacterCount = Math.max(0, characterCount - 1);
          if (customCharacterCount >= limit) {
            setLimitReached(true);
          } else {
            setLimitReached(false);
          }
        }
      }
    }
  }, [isOpen, appConfig, trialStatus, characterCount, characterToEdit]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTagToggle = (tag) => {
    setFormData(prev => {
      const current = prev.personalityTags || [];
      if (current.includes(tag)) {
        return { ...prev, personalityTags: current.filter(t => t !== tag) };
      }
      if (current.length >= 3) return prev;
      return { ...prev, personalityTags: [...current, tag] };
    });
  };

  const handleGeneratePhoto = async () => {
    if (!formData.name || !formData.sex || !formData.age) {
      setError('Name, Sex, and Age are required to generate a photo.');
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      const token = await firebaseUser.getIdToken();
      const baseParams = {
        characterId: formData.characterId,
        sex: formData.sex,
        age: formData.age,
        race: formData.ethnicity,
        hairColor: formData.hairColor,
        hairType: formData.hairStyle,
        eyeColor: formData.eyeColor,
        skinColor: formData.skinColor,
        bodyType: formData.bodyType,
        clothes: formData.clothes,
        looks: formData.looks,
        description: formData.description,
        artStyle: formData.artStyle,
        width: 1024,
        height: 1024
      };

      // Generate Portrait (Avatar)
      const portraitParams = { ...baseParams, shotType: 'portrait' };
      const portraitResult = await generateCharacterPhoto(token, portraitParams);

      // Generate Full Body (Tall)
      // Note: Venice AI (qwen-image) has a max dimension limit of 1280
      const fullParams = { ...baseParams, shotType: 'full', height: 1280 }; 
      const fullResult = await generateCharacterPhoto(token, fullParams);
      
      if (portraitResult.success && portraitResult.images.length > 0 && fullResult.success && fullResult.images.length > 0) {
        // Use thumbnailUrl for portrait avatar if available, fallback to full res url
        const newPhotoUrl = portraitResult.images[0].thumbnailUrl || portraitResult.images[0].url;
        
        // Use original full resolution url for the tall character photo
        const newFullPhotoUrl = fullResult.images[0].url;
        
        // Capture old URLs for deletion (if editing)
        const oldPhotoUrl = formData.photoUrl;
        const oldFullPhotoUrl = formData.fullPhotoUrl;

        setFormData(prev => ({ 
          ...prev, 
          photoUrl: newPhotoUrl,
          fullPhotoUrl: newFullPhotoUrl
        }));

        // If editing an existing character, update backend immediately and cleanup old photos
        if (characterToEdit) {
          try {
            // Construct updated character data
            const effectiveRoleForPhoto = formData.relationshipRole === 'other' ? formData.customRole.trim() : formData.relationshipRole;
            const updatedCharacterData = {
              characterId: formData.characterId,
              name: formData.name,
              age: formData.age,
              occupation: formData.occupation,
              personality: formData.personality,
              personalityTags: formData.personalityTags,
              language: formData.language,
              ethnicity: formData.ethnicity,
              artStyle: formData.artStyle,
              relationshipRole: effectiveRoleForPhoto,
              backstory: formData.backstory,
              photoUrl: newPhotoUrl,
              fullPhotoUrl: newFullPhotoUrl,
              physicalTraits: {
                sex: formData.sex,
                hairColor: formData.hairColor,
                hairStyle: formData.hairStyle,
                eyeColor: formData.eyeColor,
                skinColor: formData.skinColor,
                bodyType: formData.bodyType,
                clothes: formData.clothes,
                looks: formData.looks
              }
            };

            // Update character in DB
            const updatedChar = await updateCharacter(token, characterToEdit.characterId, updatedCharacterData);
            
            // Notify parent to update list
            if (onCharacterUpdated) {
              onCharacterUpdated(updatedChar);
            }

            // Delete old photos if they exist and are different
            if (oldPhotoUrl && oldPhotoUrl !== newPhotoUrl) {
              console.log('Deleting old portrait:', oldPhotoUrl);
              deletePhoto(token, oldPhotoUrl).catch(e => console.warn('Failed to delete old portrait', e));
            }
            if (oldFullPhotoUrl && oldFullPhotoUrl !== newFullPhotoUrl) {
              console.log('Deleting old full body:', oldFullPhotoUrl);
              deletePhoto(token, oldFullPhotoUrl).catch(e => console.warn('Failed to delete old full body', e));
            }

          } catch (updateErr) {
            console.error('Failed to update character after generation:', updateErr);
            // We don't stop the flow, but log the error
          }
        }

        setStep(2); // Move to preview step
        setCurrentImageIndex(0); // Reset to first image

        // Track newly generated URLs for potential orphan cleanup (create mode only).
        if (!characterToEdit) {
          if (newPhotoUrl) sessionGeneratedUrls.current.push(newPhotoUrl);
          if (newFullPhotoUrl) sessionGeneratedUrls.current.push(newFullPhotoUrl);
        }
      } else {
        throw new Error('Failed to generate one or both images');
      }

    } catch (err) {
      console.error('Error generating photo:', err);
      setError(err.message || 'Failed to generate photo');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (limitReached && !characterToEdit) return;
    
    setIsLoading(true);
    setError('');

    try {
      const token = await firebaseUser.getIdToken();
      // Clean up data before sending
      const effectiveRole = formData.relationshipRole === 'other' ? formData.customRole.trim() : formData.relationshipRole;
      const characterData = {
        characterId: formData.characterId,
        name: formData.name,
        age: formData.age,
        occupation: formData.occupation,
        personality: formData.personality,
        personalityTags: formData.personalityTags,
        language: formData.language,
        scenario: formData.scenario,
        examples: formData.examples.map(ex => ({ user: ex.user || '', char: ex.char || '' })),
        photoUrl: formData.photoUrl,
        fullPhotoUrl: formData.fullPhotoUrl,
        ethnicity: formData.ethnicity,
        artStyle: formData.artStyle,
        relationshipRole: effectiveRole,
        backstory: formData.backstory,
        physicalTraits: {
            sex: formData.sex,
            hairColor: formData.hairColor,
            hairStyle: formData.hairStyle,
            eyeColor: formData.eyeColor,
            skinColor: formData.skinColor,
            bodyType: formData.bodyType,
            clothes: formData.clothes,
            looks: formData.looks
        }
      };

      if (characterToEdit) {
        const updatedCharacter = await updateCharacter(token, characterToEdit.characterId, characterData);
        if (onCharacterUpdated) onCharacterUpdated(updatedCharacter);
      } else {
        const newCharacter = await createCharacter(token, characterData);
        // Mark these URLs as saved so orphan cleanup skips them.
        savedPhotoUrls.current.add(characterData.photoUrl);
        savedPhotoUrls.current.add(characterData.fullPhotoUrl);
        // Silently generate backstory in background — non-blocking, no await
        generateBackstory(token, newCharacter.characterId).catch(err =>
          console.warn('[CharacterModal] Backstory generation failed silently:', err)
        );
        onCharacterCreated(newCharacter);
      }
      
      onClose();
      
    } catch (err) {
      console.error('Error saving character:', err);
      setError(err.message || 'Failed to save character');
    } finally {
      setIsLoading(false);
    }
  };

  // Wraps the parent onClose handler. Deletes any in-session generated photos that
  // were never committed to a character document (i.e. user closed without saving).
  const handleClose = async () => {
    const urlsToDelete = sessionGeneratedUrls.current.filter(
      (url) => !savedPhotoUrls.current.has(url)
    );
    if (urlsToDelete.length > 0) {
      try {
        const token = await firebaseUser.getIdToken();
        urlsToDelete.forEach((url) => {
          deletePhoto(token, url).catch((e) =>
            console.warn('[CharacterModal] Orphan cleanup failed for', url, e)
          );
        });
      } catch (e) {
        console.warn('[CharacterModal] Could not get token for orphan cleanup:', e);
      }
    }
    onClose();
  };

  // Render Steps
  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-bela-500 focus:border-bela-500"
            placeholder="e.g. Sarah"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Age *</label>
          <input
            type="number"
            name="age"
            value={formData.age}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-bela-500 focus:border-bela-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Sex *</label>
        <select
          name="sex"
          value={formData.sex}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-bela-500 focus:border-bela-500"
        >
          <option value="Female">Female</option>
          <option value="Male">Male</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Art Style</label>
        <div className="grid grid-cols-4 gap-2">
          {ART_STYLES.map(style => (
            <button
              key={style.value}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, artStyle: style.value }))}
              className={`flex flex-col items-center justify-center py-2 px-1 rounded-lg border-2 text-xs font-medium transition-colors ${
                formData.artStyle === style.value
                  ? 'border-bela-600 bg-bela-50 text-bela-700'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              <span className="text-lg mb-1">{style.icon}</span>
              {style.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Ethnicity</label>
          <input
            type="text"
            name="ethnicity"
            value={formData.ethnicity}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-bela-500 focus:border-bela-500"
            placeholder="e.g. Asian, Latina"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Skin Tone</label>
          <input
            type="text"
            name="skinColor"
            value={formData.skinColor}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-bela-500 focus:border-bela-500"
            placeholder="e.g. Fair, Tan, Dark"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Hair Color</label>
          <input
            type="text"
            name="hairColor"
            value={formData.hairColor}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-bela-500 focus:border-bela-500"
            placeholder="e.g. Blonde, Black"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Hair Style</label>
          <input
            type="text"
            name="hairStyle"
            value={formData.hairStyle}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-bela-500 focus:border-bela-500"
            placeholder="e.g. Long, Bob, Curly"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Eye Color</label>
          <input
            type="text"
            name="eyeColor"
            value={formData.eyeColor}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-bela-500 focus:border-bela-500"
            placeholder="e.g. Blue, Brown"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Body Type</label>
          <input
            type="text"
            name="bodyType"
            value={formData.bodyType}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-bela-500 focus:border-bela-500"
            placeholder="e.g. Slim, Curvy"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">General Looks</label>
        <textarea
          name="looks"
          value={formData.looks}
          onChange={handleChange}
          rows="2"
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-bela-500 focus:border-bela-500"
          placeholder="e.g. Beautiful, cute girl-next-door, average-looking, ruggedly handsome"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Clothing Style</label>
        <textarea
          name="clothes"
          value={formData.clothes}
          onChange={handleChange}
          rows="2"
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-bela-500 focus:border-bela-500"
          placeholder="e.g. Casual, Business, Elegant"
        />
      </div>

      <div className="flex flex-col gap-3 mt-4">
        {characterToEdit && (
          <div className="flex gap-3">
             <button
               type="button"
               onClick={() => setStep(2)}
               className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-semibold"
             >
               View Photos
             </button>
             <button
               type="button"
               onClick={() => setStep(3)}
               className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-semibold"
             >
               Edit Personality
             </button>
          </div>
        )}
        <button
          type="button"
          onClick={handleGeneratePhoto}
          disabled={isGenerating || !formData.name}
          className="w-full bg-bela-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-bela-700 transition-colors disabled:opacity-50 flex justify-center items-center gap-2"
        >
          {isGenerating ? (
            <>
              <Spinner size="small" color="text-white" />
              Generating...
            </>
          ) : (
            <>
              <FiCamera />
              {characterToEdit ? 'Regenerate Photos' : 'Generate Photos'}
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => {
    const images = [
      { url: formData.photoUrl, label: 'Avatar (Portrait)' },
      { url: formData.fullPhotoUrl, label: 'Full Body' }
    ].filter(img => img.url); // Only show images that exist

    const currentImage = images[currentImageIndex];

    const nextImage = () => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    };

    const prevImage = () => {
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    return (
      <div className="space-y-4 text-center">
        <h3 className="text-lg font-medium text-gray-800">Here is {formData.name}!</h3>
        
        {/* Carousel */}
        <div className="relative w-full max-w-xs mx-auto h-80 bg-gray-100 rounded-lg overflow-hidden shadow-lg group">
          {currentImage ? (
            <>
              <img 
                src={currentImage.url} 
                alt={currentImage.label} 
                className="w-full h-full object-contain cursor-pointer hover:opacity-95 transition-opacity"
                onClick={() => setPreviewImage(currentImage.url)}
              />
              <div className="absolute top-0 left-0 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded-br-lg">
                {currentImage.label}
              </div>
              
              {/* Navigation Arrows */}
              {images.length > 1 && (
                <>
                  <button 
                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-30 hover:bg-opacity-50 text-white p-2 rounded-full transition-all opacity-0 group-hover:opacity-100"
                  >
                    <FiChevronLeft size={24} />
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-30 hover:bg-opacity-50 text-white p-2 rounded-full transition-all opacity-0 group-hover:opacity-100"
                  >
                    <FiChevronRight size={24} />
                  </button>
                  
                  {/* Dots Indicator */}
                  <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex gap-2">
                    {images.map((_, idx) => (
                      <div 
                        key={idx} 
                        className={`w-2 h-2 rounded-full transition-colors ${idx === currentImageIndex ? 'bg-white' : 'bg-white bg-opacity-50'}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400 flex-col gap-2">
              <FiUser size={48} />
              <span className="text-sm">No photos generated yet</span>
            </div>
          )}
        </div>

        <div className="flex gap-3 justify-center mt-4">
          <button
            type="button"
            onClick={() => setStep(1)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <FiArrowLeft />
            Edit Details
          </button>
          <button
            type="button"
            onClick={handleGeneratePhoto}
            disabled={isGenerating}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            {isGenerating ? <Spinner size="small" /> : <FiRefreshCw />}
            Regenerate
          </button>
        </div>

        <button
          type="button"
          onClick={() => setStep(3)}
          className="w-full bg-bela-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-bela-700 transition-colors flex justify-center items-center gap-2 mt-4"
        >
          Looks Good! Continue <FiArrowRight />
        </button>
      </div>
    );
  };

  const renderStep3 = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Occupation</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FiBriefcase className="text-gray-400" />
          </div>
          <input
            type="text"
            name="occupation"
            value={formData.occupation}
            onChange={handleChange}
            className="pl-10 w-full p-2 border border-gray-300 rounded-lg focus:ring-bela-500 focus:border-bela-500"
            placeholder="e.g. Teacher"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FiGlobe className="text-gray-400" />
          </div>
          <input
            type="text"
            name="language"
            value={formData.language}
            onChange={handleChange}
            className="pl-10 w-full p-2 border border-gray-300 rounded-lg focus:ring-bela-500 focus:border-bela-500"
            placeholder="e.g. French"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Relationship Role
        </label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { value: 'romantic partner', label: '💕 Romantic Partner' },
            { value: 'best friend', label: '👯 Best Friend' },
            { value: 'mentor', label: '🧠 Mentor' },
            { value: 'rival', label: '😈 Rival' },
          ].map(role => (
            <button
              key={role.value}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, relationshipRole: role.value }))}
              className={`p-2 rounded-lg border-2 text-sm font-medium text-left transition-colors ${
                formData.relationshipRole === role.value
                  ? 'border-bela-600 bg-bela-50 text-bela-700'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              {role.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, relationshipRole: 'other' }))}
            className={`col-span-2 p-2 rounded-lg border-2 text-sm font-medium text-left transition-colors ${
              formData.relationshipRole === 'other'
                ? 'border-bela-600 bg-bela-50 text-bela-700'
                : 'border-gray-200 text-gray-600 hover:border-gray-300'
            }`}
          >
            ✍️ Other (custom role)
          </button>
        </div>
        {formData.relationshipRole === 'other' && (
          <input
            type="text"
            name="customRole"
            value={formData.customRole}
            onChange={handleChange}
            className="mt-2 w-full p-2 border border-gray-300 rounded-lg focus:ring-bela-500 focus:border-bela-500 text-sm"
            placeholder="e.g. therapist, coworker, tutor..."
          />
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Personality Tags <span className="text-gray-400 font-normal">(pick up to 3)</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {PERSONALITY_TAGS.map(tag => {
            const selected = (formData.personalityTags || []).includes(tag);
            const maxReached = (formData.personalityTags || []).length >= 3 && !selected;
            return (
              <button
                key={tag}
                type="button"
                onClick={() => handleTagToggle(tag)}
                disabled={maxReached}
                className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                  selected
                    ? 'bg-bela-600 text-white border-bela-600'
                    : maxReached
                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-bela-400'
                }`}
              >
                {tag}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Personality & Traits <span className="text-gray-400 font-normal">(optional extra detail)</span></label>
        <div className="relative">
          <div className="absolute top-3 left-3 pointer-events-none">
            <FiSmile className="text-gray-400" />
          </div>
          <textarea
            name="personality"
            value={formData.personality}
            onChange={handleChange}
            rows="3"
            className="pl-10 w-full p-2 border border-gray-300 rounded-lg focus:ring-bela-500 focus:border-bela-500"
            placeholder="Add extra personality detail..."
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Scenario</label>
        <textarea
          name="scenario"
          value={formData.scenario}
          onChange={handleChange}
          rows="2"
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-bela-500 focus:border-bela-500 text-sm"
          placeholder="e.g. You are sitting together at a quiet coffee shop on a rainy afternoon."
        />
        <p className="text-xs text-gray-400 mt-1">Describes the current setting and situation to anchor the conversation.</p>
      </div>

      {characterToEdit && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Backstory <span className="text-gray-400 font-normal text-xs">(AI-generated, editable)</span>
          </label>
          <textarea
            name="backstory"
            value={formData.backstory}
            onChange={handleChange}
            rows="3"
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-bela-500 focus:border-bela-500 text-sm"
            placeholder="Backstory will appear here after the character is created..."
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Example Dialogues</label>
        <p className="text-xs text-gray-400 mb-3">Show how {formData.name || 'your character'} should respond by providing examples. These train the AI's tone and style better than rules.</p>
        {formData.examples.map((ex, i) => (
          <div key={i} className="mb-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
            <p className="text-xs font-medium text-gray-500 mb-2">Example {i + 1}</p>
            <div className="space-y-2">
              <input
                type="text"
                placeholder="User says..."
                value={ex.user}
                onChange={(e) => {
                  const next = [...formData.examples];
                  next[i] = { ...next[i], user: e.target.value };
                  setFormData(prev => ({ ...prev, examples: next }));
                }}
                className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-bela-500 focus:border-bela-500"
              />
              <input
                type="text"
                placeholder={`${formData.name || 'Character'} responds...`}
                value={ex.char}
                onChange={(e) => {
                  const next = [...formData.examples];
                  next[i] = { ...next[i], char: e.target.value };
                  setFormData(prev => ({ ...prev, examples: next }));
                }}
                className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-bela-500 focus:border-bela-500"
              />
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-3 mt-6">
        <button
          type="button"
          onClick={() => setStep(2)}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="flex-1 bg-bela-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-bela-700 transition-colors disabled:opacity-50 flex justify-center items-center gap-2"
        >
          {isLoading ? <Spinner size="small" color="text-white" /> : (characterToEdit ? 'Update Character' : 'Create Character')}
        </button>
      </div>
    </form>
  );

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
      >
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">
              {characterToEdit ? 'Edit Character' : 'Create New Character'}
              {step === 1 && ': Appearance'}
              {step === 2 && ': Photos'}
              {step === 3 && ': Personality'}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
              aria-label="Close"
            >
              <FiX size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {limitReached && !characterToEdit ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 text-center">
                <FiAlertCircle className="mx-auto text-yellow-500 mb-2" size={32} />
                <h3 className="text-lg font-medium text-yellow-800 mb-1">Character Limit Reached</h3>
                <p className="text-yellow-700 text-sm mb-3">
                  {trialStatus.isPaidUser 
                    ? `You have reached the limit of ${appConfig?.limits?.paid?.customCharacterLimit ?? 20} characters.`
                    : `Free users can only create ${appConfig?.limits?.free?.customCharacterLimit ?? 1} custom character.`}
                </p>
                {!trialStatus.isPaidUser && (
                  <button
                    onClick={() => {
                      onClose();
                    }}
                    className="bg-bela-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-bela-700 transition-colors text-sm"
                  >
                    Upgrade to Create More
                  </button>
                )}
              </div>
            ) : (
              <>
                {error && (
                  <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm mb-4">
                    {error}
                  </div>
                )}
                
                {/* Progress Bar */}
                <div className="flex gap-2 mb-6">
                  <div className={`h-1 flex-1 rounded-full ${step >= 1 ? 'bg-bela-600' : 'bg-gray-200'}`}></div>
                  <div className={`h-1 flex-1 rounded-full ${step >= 2 ? 'bg-bela-600' : 'bg-gray-200'}`}></div>
                  <div className={`h-1 flex-1 rounded-full ${step >= 3 ? 'bg-bela-600' : 'bg-gray-200'}`}></div>
                </div>

                {step === 1 && renderStep1()}
                {step === 2 && renderStep2()}
                {step === 3 && renderStep3()}
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Full Screen Preview Modal */}
      <AvatarModal 
        isOpen={!!previewImage} 
        onClose={() => setPreviewImage(null)} 
        imageUrl={previewImage}
        altText="Character Preview"
      />
    </>
  );
};

export default CharacterModal;
